import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Breadcrumbs from '../components/Breadcrumbs';
import { Button } from '../components/ui/button';
import { Calendar, ArrowLeft, ArrowRight } from 'lucide-react';

const articulosData = {
  'subvenciones-placas-solares-espana-2026': {
    titulo: 'Subvenciones para Placas Solares en España en 2026',
    fecha: '10 de marzo, 2026',
    categoria: 'Ayudas y Subvenciones',
    imagen: 'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d',
    descripcion: 'Guía completa sobre todas las ayudas, subvenciones y deducciones fiscales disponibles para instalar placas solares en España en 2026.',
    contenido: `
      <h2>Introducción a las Ayudas para Energía Solar en España</h2>
      <p>En 2026, instalar placas solares en España cuenta con múltiples incentivos económicos que hacen de esta inversión una opción cada vez más accesible y rentable. El gobierno español y las comunidades autónomas mantienen su compromiso con la transición energética, ofreciendo diversas ayudas que pueden reducir significativamente el coste inicial de tu instalación fotovoltaica.</p>

      <h2>Deducción en el IRPF (Hasta 40%)</h2>
      <p>Una de las ayudas más importantes es la <strong>deducción fiscal en el IRPF</strong>, que permite deducir hasta el 40% del coste de la instalación en tu declaración de la renta. Esta deducción está disponible para actuaciones que mejoren la eficiencia energética de tu vivienda habitual.</p>
      
      <h3>Requisitos para la Deducción del IRPF:</h3>
      <ul>
        <li>Reducir al menos un 30% el consumo de energía primaria no renovable</li>
        <li>Alcanzar una calificación energética A o B</li>
        <li>Aplicable únicamente a vivienda habitual</li>
        <li>Certificado energético antes y después de la obra</li>
      </ul>

      <h2>Bonificaciones Municipales</h2>
      
      <h3>Bonificación del IBI</h3>
      <p>Muchos ayuntamientos españoles ofrecen bonificaciones en el Impuesto sobre Bienes Inmuebles (IBI) de hasta el 50% durante varios años para viviendas con instalaciones solares. En Madrid, esta bonificación puede extenderse hasta 5 años.</p>

      <h3>Bonificación del ICIO</h3>
      <p>El Impuesto sobre Construcciones, Instalaciones y Obras puede bonificarse hasta un 95% en algunos municipios. Esta ayuda se aplica en el momento de solicitar la licencia de obras, reduciendo inmediatamente el coste de la instalación.</p>

      <h2>Subvenciones Autonómicas</h2>
      <p>Las comunidades autónomas gestionan convocatorias periódicas de ayudas directas para el autoconsumo fotovoltaico. Estas subvenciones pueden cubir entre el 20% y el 50% del coste total de la instalación, dependiendo de:</p>
      <ul>
        <li>La potencia instalada (€/kWp)</li>
        <li>El tipo de instalación (residencial, empresarial, autoconsumo colectivo)</li>
        <li>La comunidad autónoma donde se realice la instalación</li>
      </ul>

      <h2>Ayudas para Autoconsumo Colectivo</h2>
      <p>Las instalaciones de autoconsumo colectivo (comunidades de vecinos, polígonos industriales) reciben incentivos especiales con mayor cuantía de ayuda. El gobierno está priorizando este tipo de proyectos que optimizan el uso de espacios comunes.</p>

      <h2>Cómo Solicitar las Ayudas</h2>
      <p>En <strong>Nubia Solar</strong> nos encargamos gratuitamente de tramitar todas las ayudas disponibles en tu zona:</p>
      <ol>
        <li>Análisis de las ayudas aplicables a tu caso específico</li>
        <li>Preparación de toda la documentación necesaria</li>
        <li>Presentación de solicitudes ante los organismos correspondientes</li>
        <li>Seguimiento hasta la concesión y cobro de las ayudas</li>
      </ol>

      <h2>Ejemplo de Ahorro Real con Ayudas</h2>
      <p>Para una instalación residencial típica de 5 kW en Madrid:</p>
      <ul>
        <li>Coste base: 7.000€</li>
        <li>Deducción IRPF (40%): -2.800€</li>
        <li>Subvención autonómica (30%): -2.100€</li>
        <li>Bonificación ICIO (75%): -400€</li>
        <li><strong>Coste final: 1.700€ (76% de ahorro)</strong></li>
      </ul>

      <h2>Conclusión</h2>
      <p>Las ayudas disponibles en 2026 hacen que instalar placas solares sea más asequible que nunca. Con la combinación de deducciones fiscales, bonificaciones municipales y subvenciones autonómicas, el coste real de tu instalación puede reducirse hasta un 80%.</p>
    `
  },
  'cuanto-cuesta-instalar-placas-solares-madrid': {
    titulo: 'Cuánto Cuesta Instalar Placas Solares en una Vivienda en Madrid',
    fecha: '8 de marzo, 2026',
    categoria: 'Precios y Presupuestos',
    imagen: 'https://images.unsplash.com/photo-1509391366360-2e959784a276',
    descripcion: 'Análisis completo de los costes de instalación de paneles solares en Madrid, factores que influyen en el precio y retorno de inversión.',
    contenido: `
      <h2>¿Cuánto Cuesta Instalar Placas Solares en Madrid?</h2>
      <p>El coste de instalar placas solares en una vivienda en Madrid varía según múltiples factores, pero generalmente oscila entre <strong>4.000€ y 8.000€</strong> para una instalación residencial estándar. Sin embargo, con las ayudas y subvenciones disponibles, el coste final puede reducirse significativamente.</p>

      <h2>Factores que Influyen en el Precio</h2>
      
      <h3>1. Tamaño de la Instalación</h3>
      <p>El factor más determinante es la potencia que necesitas instalar, medida en kilovatios pico (kWp). Una vivienda unifamiliar típica en Madrid requiere entre 3 y 6 kWp.</p>
      <ul>
        <li>Instalación pequeña (3 kWp): 4.000€ - 5.000€</li>
        <li>Instalación media (5 kWp): 6.000€ - 7.000€</li>
        <li>Instalación grande (8 kWp): 8.000€ - 10.000€</li>
      </ul>

      <h3>2. Calidad de los Componentes</h3>
      <p>Los paneles solares de marcas premium con mayor eficiencia tienen un coste superior, pero ofrecen mejor rendimiento y mayor garantía:</p>
      <ul>
        <li>Paneles estándar: 200€-300€ por panel</li>
        <li>Paneles premium: 300€-450€ por panel</li>
      </ul>

      <h3>3. Tipo de Tejado y Estructura</h3>
      <p>El tipo de cubierta donde se instalen los paneles afecta al coste:</p>
      <ul>
        <li>Tejado inclinado (teja): instalación estándar</li>
        <li>Tejado plano: requiere estructura adicional (+10-15%)</li>
        <li>Tejado con dificultad de acceso: puede incrementar el coste</li>
      </ul>

      <h3>4. Inversor</h3>
      <p>El inversor es el cerebro de tu instalación solar. Su coste varía según potencia y tecnología:</p>
      <ul>
        <li>Inversor string estándar: 1.000€ - 1.500€</li>
        <li>Inversor híbrido (con batería): 1.800€ - 3.000€</li>
      </ul>

      <h2>Desglose de Costes de una Instalación Típica (5 kWp)</h2>
      <ul>
        <li>Paneles solares (12-15 unidades): 2.800€</li>
        <li>Inversor: 1.200€</li>
        <li>Estructura de montaje: 800€</li>
        <li>Cableado y protecciones: 500€</li>
        <li>Instalación y mano de obra: 1.200€</li>
        <li>Tramitación y legalización: 500€</li>
        <li><strong>Total: 7.000€</strong></li>
      </ul>

      <h2>Reducción de Costes con Ayudas</h2>
      <p>En Madrid puedes beneficiarte de múltiples ayudas que reducen drásticamente la inversión inicial:</p>
      <ul>
        <li>Subvención Comunidad de Madrid (40%): -2.800€</li>
        <li>Bonificación IBI (50% durante 5 años): -500€/año</li>
        <li>Deducción IRPF (hasta 40%): -2.800€</li>
        <li>Bonificación ICIO (95%): -450€</li>
      </ul>
      <p><strong>Coste final real: 1.500€ - 2.500€</strong></p>

      <h2>Retorno de Inversión</h2>
      <p>Con los precios actuales de la electricidad en Madrid y el ahorro medio del 70% en la factura:</p>
      <ul>
        <li>Ahorro mensual medio: 80€ - 120€</li>
        <li>Ahorro anual: 960€ - 1.440€</li>
        <li>Periodo de amortización: 1-3 años</li>
        <li>Ahorro total en 25 años: +25.000€</li>
      </ul>

      <h2>Conclusión</h2>
      <p>Aunque el coste inicial de una instalación solar puede parecer elevado, las ayudas disponibles y el rápido retorno de inversión hacen que sea una decisión financieramente muy inteligente para cualquier vivienda en Madrid.</p>
    `
  },
  'cuantas-placas-solares-necesita-una-casa': {
    titulo: 'Cuántas Placas Solares Necesita una Casa',
    fecha: '5 de marzo, 2026',
    categoria: 'Guías Prácticas',
    imagen: 'https://images.pexels.com/photos/30440447/pexels-photo-30440447.jpeg',
    descripcion: 'Guía completa para calcular el número de paneles solares que necesitas según tu consumo, superficie y orientación.',
    contenido: `
      <h2>¿Cuántas Placas Solares Necesito para Mi Casa?</h2>
      <p>El número de placas solares que necesita una casa depende principalmente de tu consumo eléctrico, el espacio disponible en tu tejado y la orientación del mismo. En este artículo te explicamos cómo calcular el número exacto de paneles que necesitas.</p>

      <h2>Paso 1: Calcula Tu Consumo Energético</h2>
      <p>El primer paso es conocer tu consumo eléctrico anual. Puedes encontrar esta información en tu factura de la luz, generalmente expresada en kWh (kilovatios hora).</p>
      
      <h3>Consumo Medio por Tipo de Vivienda:</h3>
      <ul>
        <li>Piso pequeño (2 personas): 2.000-2.500 kWh/año</li>
        <li>Piso medio (3-4 personas): 3.000-4.000 kWh/año</li>
        <li>Casa unifamiliar (4-5 personas): 4.500-6.000 kWh/año</li>
        <li>Casa grande con piscina: 7.000-10.000 kWh/año</li>
      </ul>

      <h2>Paso 2: Calcula la Potencia Necesaria</h2>
      <p>Para saber la potencia de instalación que necesitas, divide tu consumo anual entre las horas de sol efectivas en tu zona.</p>
      
      <p><strong>Fórmula:</strong> Potencia necesaria (kWp) = Consumo anual (kWh) ÷ Horas de sol efectivas</p>
      
      <p>En Madrid, con aproximadamente 1.700 horas de sol efectivas al año:</p>
      <ul>
        <li>Consumo de 3.500 kWh/año → Necesitas 3.5 kWp ≈ <strong>9-10 paneles</strong></li>
        <li>Consumo de 5.000 kWh/año → Necesitas 5 kWp ≈ <strong>12-14 paneles</strong></li>
        <li>Consumo de 7.000 kWh/año → Necesitas 7 kWp ≈ <strong>17-19 paneles</strong></li>
      </ul>

      <h2>Paso 3: Verifica el Espacio Disponible</h2>
      <p>Cada panel solar ocupa aproximadamente 1.7-2 m². Necesitas espacio suficiente en tu tejado teniendo en cuenta:</p>
      <ul>
        <li>Separación entre paneles: 10-20 cm</li>
        <li>Distancia a chimeneas y obstáculos: mínimo 50 cm</li>
        <li>Zonas de sombra: evitar áreas con sombra durante el día</li>
      </ul>

      <h3>Espacio Necesario por Instalación:</h3>
      <ul>
        <li>10 paneles (3.5 kWp): 20-25 m²</li>
        <li>14 paneles (5 kWp): 28-35 m²</li>
        <li>18 paneles (7 kWp): 36-45 m²</li>
      </ul>

      <h2>Paso 4: Considera la Orientación del Tejado</h2>
      <p>La orientación e inclinación de tu tejado afectan significativamente al rendimiento:</p>
      <ul>
        <li><strong>Orientación Sur:</strong> 100% de eficiencia (ideal)</li>
        <li><strong>Orientación Sureste/Suroeste:</strong> 90-95% de eficiencia</li>
        <li><strong>Orientación Este/Oeste:</strong> 80-85% de eficiencia</li>
        <li><strong>Orientación Norte:</strong> No recomendable en España</li>
      </ul>

      <h2>Ejemplo Práctico: Familia en Madrid</h2>
      <p>Veamos un caso real de una familia de 4 personas en una vivienda unifamiliar en Madrid:</p>
      <ul>
        <li>Consumo anual: 4.500 kWh</li>
        <li>Potencia necesaria: 4.5 kWp</li>
        <li>Paneles recomendados: 11-12 paneles de 400W</li>
        <li>Espacio requerido: 25-30 m²</li>
        <li>Orientación: Sur-Suroeste</li>
        <li>Producción estimada: 5.600 kWh/año</li>
        <li>Cobertura: 124% del consumo (incluye excedentes)</li>
      </ul>

      <h2>Factores Adicionales a Considerar</h2>
      
      <h3>Consumo Futuro</h3>
      <p>Si planeas comprar un coche eléctrico o aumentar tu consumo, considera instalar más potencia ahora para no quedarte corto.</p>

      <h3>Excedentes</h3>
      <p>Producir más de lo que consumes te permite vender el excedente a la red eléctrica o acumularlo con un sistema de batería virtual.</p>

      <h3>Baterías</h3>
      <p>Si quieres máxima independencia energética, puedes añadir baterías de almacenamiento (aunque incrementan la inversión inicial).</p>

      <h2>Conclusión</h2>
      <p>El número de placas solares que necesitas depende de tu consumo específico y las características de tu vivienda. En <strong>Nubia Solar</strong> realizamos un estudio personalizado gratuito para dimensionar exactamente tu instalación, optimizando el número de paneles y maximizando tu ahorro.</p>
    `
  }
};

const BlogArticulo = () => {
  const { slug } = useParams();
  const articulo = articulosData[slug];

  if (!articulo) {
    return <div>Artículo no encontrado</div>;
  }

  const pageTitle = `${articulo.titulo} | Blog Nubia Solar`;

  const breadcrumbItems = [
    { label: 'Inicio', href: '/' },
    { label: 'Blog', href: '/blog' },
    { label: articulo.titulo, href: '' }
  ];
  
  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={articulo.descripcion} />
      </Helmet>

      <Breadcrumbs items={breadcrumbItems} />

      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-20 bg-nubia-gray-light">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Link to="/blog" className="inline-flex items-center text-nubia-green-support hover:text-nubia-green-primary mb-6 font-medium">
                <ArrowLeft className="mr-2 w-4 h-4" />
                Volver al Blog
              </Link>
              <div className="text-sm text-nubia-gold-primary font-semibold mb-4">
                {articulo.categoria}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-6">
                {articulo.titulo}
              </h1>
              <div className="flex items-center gap-2 text-nubia-text-dark/60">
                <Calendar className="w-5 h-5" />
                <span>{articulo.fecha}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="py-0">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <img 
                src={articulo.imagen} 
                alt={articulo.titulo}
                className="w-full h-[400px] object-cover rounded-2xl shadow-2xl -mt-12"
              />
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto prose prose-lg max-w-none">
              <div 
                className="text-nubia-text-dark/80 leading-relaxed"
                style={{
                  fontSize: '1.125rem',
                  lineHeight: '1.8'
                }}
                dangerouslySetInnerHTML={{ __html: articulo.contenido }}
              />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-nubia-gray-light">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-nubia-green-primary mb-6">
                ¿Listo para Instalar Placas Solares?
              </h2>
              <p className="text-lg text-nubia-text-dark/70 mb-8">
                Solicita tu estudio solar gratuito y personalizado
              </p>
              <Link to="/contacto">
                <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-6">
                  Solicitar Presupuesto Gratis
                  <ArrowRight className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogArticulo;
